import { AnnotatedFigure } from '@/modules/marketing/AnnotatedFigure';
import { stepAnchor, type Guide } from '@/modules/marketing/guides';

/**
 * A guide's numbered steps, with their figures.
 *
 * Shared by the public guide page and the dashboard's Docs, so a customer reading a guide while
 * signed in sees exactly the steps a visitor does.
 */
export function GuideSteps({ guide, className = 'mt-14' }: { guide: Guide; className?: string }) {
  return (
    <ol className={`relative ${className}`}>
      {/* The thread through the steps. Sits under the badges, hence the ring on each. */}
      <span
        aria-hidden
        className="bg-border absolute top-3 bottom-3 left-[17px] hidden w-px sm:block"
      />

      {guide.steps.map((step, index) => (
        <li
          key={step.title}
          id={stepAnchor(step.title)}
          className="relative scroll-mt-28 pb-14 last:pb-0 sm:pl-14"
        >
          <span
            aria-hidden
            className="bg-primary text-primary-foreground ring-background absolute top-0 left-0 hidden size-9 items-center justify-center rounded-full text-sm font-semibold ring-4 sm:flex"
          >
            {index + 1}
          </span>

          <div className="mb-3 flex items-center gap-3 sm:hidden">
            <span className="bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-full text-xs font-semibold">
              {index + 1}
            </span>
          </div>

          <h2 className="text-xl font-semibold tracking-tight sm:pt-1">{step.title}</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">{step.body}</p>

          {step.image !== undefined && (
            <div className="mt-6">
              <AnnotatedFigure
                caption={step.caption ?? step.title}
                callouts={step.callouts ?? []}
              >
                <img
                  src={step.image}
                  alt={step.caption ?? step.title}
                  // Intrinsic size, so the box exists before the file arrives.
                  width={step.width}
                  height={step.height}
                  className="block h-auto w-full"
                  // The first figure sits above the fold, and it is the thing the reader
                  // opened the page for. Deferring that one costs more than the rest save.
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </AnnotatedFigure>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
